/**
 * The codec for every branded id of `./entity-id.ts`.
 *
 * `parse` is the only sanctioned way to turn a string from a wire, a row or an environment
 * into an id, and it throws rather than returning a fallback (design 9.6).
 */
import { idCodec } from "./brand.js";
// ---- Tenancy (design 4.5) ----------------------------------------
export const accountId = idCodec("AccountId");
export const workspaceId = idCodec("WorkspaceId");
export const membershipId = idCodec("MembershipId");
export const invitationId = idCodec("InvitationId");
export const apiKeyId = idCodec("ApiKeyId");
export const webhookEndpointId = idCodec("WebhookEndpointId");
export const webhookDeliveryAttemptId = idCodec("WebhookDeliveryAttemptId");
export const outboxMessageId = idCodec("OutboxMessageId");
export const inboundWebhookEventId = idCodec("InboundWebhookEventId");
export const featureToggleId = idCodec("FeatureToggleId");
export const featureToggleAccountId = idCodec("FeatureToggleAccountId");
// `exportId` is NOT declared here: `ExportId` is an alias of `PersonDataRequestId`
// (AMENDED 2026-09-22, P6-01, Issue 4d), so it shares that entity's single codec. The alias
// is exported beside `personDataRequestId` further down, because a `const` cannot be aliased
// before the binding it names is initialised.
// ---- Identity (design 4.15, 8) -----------------------------------
export const personId = idCodec("PersonId");
export const personCredentialId = idCodec("PersonCredentialId");
export const personTokenId = idCodec("PersonTokenId");
export const externalIdentityId = idCodec("ExternalIdentityId");
export const authSessionId = idCodec("AuthSessionId");
export const identityProviderId = idCodec("IdentityProviderId");
export const ssoDomainId = idCodec("SsoDomainId");
export const breakGlassGrantId = idCodec("BreakGlassGrantId");
export const operatorId = idCodec("OperatorId");
export const operatorCredentialId = idCodec("OperatorCredentialId");
export const operatorMfaFactorId = idCodec("OperatorMfaFactorId");
export const operatorRecoveryCodeId = idCodec("OperatorRecoveryCodeId");
export const operatorSessionId = idCodec("OperatorSessionId");
export const impersonationGrantId = idCodec("ImpersonationGrantId");
export const operatorActionId = idCodec("OperatorActionId");
export const supportNoteId = idCodec("SupportNoteId");
export const sandboxSessionId = idCodec("SandboxSessionId");
// ---- VoiceAgent, versions and the factory (design 4.6) -----------
export const voiceAgentId = idCodec("VoiceAgentId");
export const agentVersionId = idCodec("AgentVersionId");
export const agentDocumentId = idCodec("AgentDocumentId");
export const connectionId = idCodec("ConnectionId");
export const connectionOperationId = idCodec("ConnectionOperationId");
export const buildJobId = idCodec("BuildJobId");
export const buildStepId = idCodec("BuildStepId");
export const extractionCheckRoundId = idCodec("ExtractionCheckRoundId");
export const extractionRunId = idCodec("ExtractionRunId");
export const gapId = idCodec("GapId");
export const testSuiteId = idCodec("TestSuiteId");
export const testCaseId = idCodec("TestCaseId");
export const testRunId = idCodec("TestRunId");
export const testRunResultId = idCodec("TestRunResultId");
export const optimisationReviewId = idCodec("OptimisationReviewId");
export const optimisationFindingId = idCodec("OptimisationFindingId");
export const optimisationReviewModelUsageId = idCodec("OptimisationReviewModelUsageId");
// ---- Channels (design 4.7) ---------------------------------------
export const channelBindingId = idCodec("ChannelBindingId");
export const phoneNumberId = idCodec("PhoneNumberId");
export const sipCredentialId = idCodec("SipCredentialId");
export const signallingEventId = idCodec("SignallingEventId");
export const regionLatencySampleId = idCodec("RegionLatencySampleId");
// ---- Platform catalogue (design 4.6, 4.4) ------------------------
export const agentTemplateId = idCodec("AgentTemplateId");
export const languagePackId = idCodec("LanguagePackId");
export const voiceId = idCodec("VoiceId");
export const acknowledgementSetId = idCodec("AcknowledgementSetId");
// ---- Conversation and evidence (design 4.8) ----------------------
export const conversationId = idCodec("ConversationId");
export const turnId = idCodec("TurnId");
export const transcriptId = idCodec("TranscriptId");
export const recordingId = idCodec("RecordingId");
export const conversationEventId = idCodec("ConversationEventId");
export const conversationFlagId = idCodec("ConversationFlagId");
export const overflowEventId = idCodec("OverflowEventId");
// ---- Cost of goods (design 4.11) ---------------------------------
export const modelUsageId = idCodec("ModelUsageId");
export const speechUsageId = idCodec("SpeechUsageId");
export const carrierUsageId = idCodec("CarrierUsageId");
export const conversationCostId = idCodec("ConversationCostId");
export const costRateId = idCodec("CostRateId");
// ---- Billing (design 4.11) ---------------------------------------
export const billingAccountId = idCodec("BillingAccountId");
export const ledgerEntryId = idCodec("LedgerEntryId");
export const paymentMethodId = idCodec("PaymentMethodId");
export const autoReloadPolicyId = idCodec("AutoReloadPolicyId");
export const budgetId = idCodec("BudgetId");
export const chargeAttemptId = idCodec("ChargeAttemptId");
export const invoiceId = idCodec("InvoiceId");
export const trialGrantId = idCodec("TrialGrantId");
export const quotaRequestId = idCodec("QuotaRequestId");
export const usageDailyId = idCodec("UsageDailyId");
export const priceBookId = idCodec("PriceBookId");
export const tierId = idCodec("TierId");
// ---- Outbound and compliance (design 4.10) -----------------------
export const contactListId = idCodec("ContactListId");
export const contactId = idCodec("ContactId");
export const campaignId = idCodec("CampaignId");
export const campaignQuestionId = idCodec("CampaignQuestionId");
export const campaignAttemptId = idCodec("CampaignAttemptId");
export const campaignResponseId = idCodec("CampaignResponseId");
export const surveyRoundId = idCodec("SurveyRoundId");
export const doNotCallEntryId = idCodec("DoNotCallEntryId");
export const optOutRequestId = idCodec("OptOutRequestId");
export const consentLineId = idCodec("ConsentLineId");
export const retentionPolicyId = idCodec("RetentionPolicyId");
export const auditEntryId = idCodec("AuditEntryId");
export const personDataRequestId = idCodec("PersonDataRequestId");
/**
 * `ExportId` ≡ `PersonDataRequestId` (design 3.5, amended 2026-09-22 run 2; Issue 4d closed by
 * P6-01). The blob under the `export` container is named for the request that produced it, so
 * the id has exactly one owning table and exactly one codec. Two codecs would mint two
 * populations of "export id" that no query could join.
 */
export const exportId = personDataRequestId;
/**
 * ADDED 2026-09-22 (plan-002 RB-03, design §15.5.5 DD-5). A synchronous export artefact's own
 * codec: NOT an alias of `exportId`, because an artefact is not a person-data request and a
 * shared codec would let one population of ids stand in for the other.
 */
export const exportArtefactId = idCodec("ExportArtefactId");
// ---- The order book (design 4.12) --------------------------------
export const shopId = idCodec("ShopId");
export const customerOrderId = idCodec("CustomerOrderId");
export const orderLineId = idCodec("OrderLineId");
export const orderStatusTransitionId = idCodec("OrderStatusTransitionId");
export const orderTicketId = idCodec("OrderTicketId");
export const shopCustomerId = idCodec("ShopCustomerId");
export const shopCustomerAddressId = idCodec("ShopCustomerAddressId");
export const couponId = idCodec("CouponId");
export const couponRedemptionId = idCodec("CouponRedemptionId");
// ---- Capacity and infrastructure (design 4.13) -------------------
export const gpuPoolId = idCodec("GpuPoolId");
export const gpuReplicaId = idCodec("GpuReplicaId");
export const capacitySnapshotId = idCodec("CapacitySnapshotId");
// ---- Online payment, payables, SMS, one conversation (plan-005 OP-11, design §18.3.4) ----
export const customerOrderPaymentId = idCodec("CustomerOrderPaymentId");
export const customerOrderRefundId = idCodec("CustomerOrderRefundId");
export const customerOrderDisputeId = idCodec("CustomerOrderDisputeId");
export const customerOrderPaymentEventId = idCodec("CustomerOrderPaymentEventId");
export const payableEntryId = idCodec("PayableEntryId");
export const payoutId = idCodec("PayoutId");
export const payoutRunId = idCodec("PayoutRunId");
export const payoutDestinationId = idCodec("PayoutDestinationId");
export const smsMessageId = idCodec("SmsMessageId");
export const conversationSegmentId = idCodec("ConversationSegmentId");
export const collectionExceptionId = idCodec("CollectionExceptionId");
export const regulatorySignOffId = idCodec("RegulatorySignOffId");
// ---- Customer SIP trunks (plan-008 ST-DB, design §22.4; SipLegKey is not a UUID and has none) ----
export const sipTrunkId = idCodec("SipTrunkId");
export const sipTrunkEventId = idCodec("SipTrunkEventId");
//# sourceMappingURL=codec.js.map